import {
    ActionButton,
    Styler,
    Divider2,
    Stacker,
    useBreakpoints,
    TypographyBody,
    UnsafeHTML,
    Popover,
    PopoverAnchor,
    PopoverPanel,
    Paper,
} from '@honeycomb/ui-core';
import { PhoneIcon } from 'components/icons';
import React from 'react';
import { useGlobalContext } from '@honeycomb/data';
import { PhoneIcon as PhoneIconType, OpeningHoursDay, replaceResourceString } from './LayoutUtil';

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const calcMinutes = (hours: number, minutes: number): number => hours * 60 + minutes;

function formatTime(args: { hours: number; minutes: number }): string {
    const { hours, minutes } = args;
    const pad = (number: number) => {
        return number.toString().padStart(2, '0');
    };
    return `${pad(hours)}:${pad(minutes)}`;
}

export function OpeningHours(props: { days: OpeningHoursDay[]; open: boolean; content: PhoneIconType }) {
    const {
        days,
        open,
        content: {
            phoneIcon: {
                modalContent: { newBooking, callChargesInfo, existingBooking },
            },
        },
    } = props;

    return (
        <Styler p={4} c="text.inverse" w={280}>
            <TypographyBody component="h2" size="l" mb={3}>
                <UnsafeHTML>{open ? newBooking.openHeading : newBooking.closeHeading}</UnsafeHTML>
            </TypographyBody>
            <ul>
                {days.map((day) => {
                    const times = (): string => {
                        if (day.closed) {
                            return 'Closed';
                        }
                        return `${formatTime(day.opening)} - ${formatTime(day.closing)}`;
                    };
                    return (
                        <>
                            <Stacker component="li" direction="row" justifyContent="space-between">
                                <span>{daysOfWeek[day.dayOfWeek]}</span>
                                <TypographyBody component="span" color="primary.base" weight="bold">
                                    {times()}
                                </TypographyBody>
                            </Stacker>
                            <Divider2 my={2} component="li" />
                        </>
                    );
                })}
            </ul>
            <TypographyBody my={3} align="center">
                {callChargesInfo}
            </TypographyBody>
            <TypographyBody my={2}>
                {existingBooking.heading} <strong>{existingBooking.telephoneNumber}</strong>.
            </TypographyBody>
            <ul>
                {existingBooking.timings.map((timing, index) => {
                    return (
                        <>
                            <Stacker component="li" direction="row" justifyContent="space-between">
                                <span>{timing.day}</span>
                                <TypographyBody component="span" color="primary.base" weight="bold">
                                    {timing.timing}
                                </TypographyBody>
                            </Stacker>
                            {index !== existingBooking.timings.length - 1 && <Divider2 my={2} component="li" />}
                        </>
                    );
                })}
            </ul>
        </Styler>
    );
}

export function CallUsButton({ phoneIcon }: PhoneIconType): React.JSX.Element {
    const {
        configSettings: { HCTimeZone, HCResponseTapClassName },
        resourceStrings: { HC_CALLTOBOOKTELEPHONENUMBER },
    } = useGlobalContext();

    const breakpoints = useBreakpoints();

    const {
        modalContent: {
            newBooking: { openingHours },
        },
        text: telephoneNumber,
    } = phoneIcon;

    const [open, setOpen] = React.useState<boolean>(false);

    const now = new Date();
    const dayOfWeek = now.getDay();
    const todaysHours = openingHours[dayOfWeek];

    const areLinesOpen = (): boolean => {
        const time = now
            .toLocaleString('en-GB', { timeZone: HCTimeZone || 'Europe/London' })
            .split(' ')[1]
            ?.split(':');
        const currentMinutes = calcMinutes(Number(time[0]), Number(time[1]));
        const { opening, closing } = todaysHours;

        const openingTime = new Date();
        openingTime.setHours(opening.hours, opening.minutes, 0);
        const closingTime = new Date();
        closingTime.setHours(closing.hours, closing.minutes, 0);

        return (
            currentMinutes >= calcMinutes(opening.hours, opening.minutes) &&
            currentMinutes < calcMinutes(closing.hours, closing.minutes)
        );
    };

    const linesOpen = areLinesOpen();

    // Rearrange the opening hours so that the current day of week is displayed first
    const first = openingHours.slice(dayOfWeek);
    const rest = openingHours.slice(0, dayOfWeek);
    const days = first.concat(rest);

    const showLabel = breakpoints.largeUp;

    return (
        <Popover open={open} onClose={() => setOpen(false)} shift={true} dismiss={true}>
            <PopoverAnchor>
                <ActionButton
                    icon={
                        <Styler pos="relative" display="flex">
                            <span
                                style={{
                                    position: 'absolute',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    top: 0,
                                    right: 0,
                                    backgroundColor: linesOpen ? '#78e835' : '#ff0000',
                                }}
                            />
                            <PhoneIcon />
                        </Styler>
                    }
                    color="text.inverse"
                    direction="row"
                    onClick={() => setOpen((curent) => !curent)}
                    round={!showLabel}
                >
                    <span id="contact-number" className={HCResponseTapClassName}>
                        {showLabel
                            ? replaceResourceString(
                                  telephoneNumber,
                                  'HC_CALLTOBOOKTELEPHONENUMBER',
                                  HC_CALLTOBOOKTELEPHONENUMBER
                              )
                            : undefined}
                    </span>
                </ActionButton>
            </PopoverAnchor>
            <PopoverPanel>
                <Paper shadow="s" radius="m" bg="grey.tint.700">
                    <OpeningHours days={days} open={linesOpen} content={{ phoneIcon }} />
                </Paper>
            </PopoverPanel>
        </Popover>
    );
}
